<ng-container *transloco="let t">
  <div class="header-wrapper">
    <mzic-header
      (downloadAppClicked)="scrollToAppDownloadSection()"></mzic-header>
  </div>

  <!-- Hero Section -->
  <section class="hero-section">
    <div class="hero-container">
      <div class="hero-content">
        <h1 class="hero-title">
          {{ t('lang.hero.title') }}
        </h1>
        <p class="hero-subtitle">
          {{ t('lang.hero.subtitle') }}
        </p>
        <mzic-button class="hero-cta-button" (click)="goToHeroCta()">
          {{ t('lang.hero.ctaButton') }}
        </mzic-button>
      </div>
    </div>
  </section>

  <!-- broker Section -->
  <section class="broker-section">
    <div class="section-container">
      <div class="broker-card">
        <div class="card-content">
          <h2 class="card-title" [innerHTML]="t('lang.broker.title')"></h2>
          <a (click)="goToAboutMzic()" class="card-link">
            {{ t('lang.broker.ctaLink') }} ↗
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Investment Opportunities Section -->
  <section class="investment-opportunities-section">
    <div class="section-container">
      <div class="opportunities-header">
        <h2 class="opportunities-title">
          {{ t('lang.investmentOpportunities.title') }}
        </h2>
        <p class="opportunities-subtitle">
          {{ t('lang.investmentOpportunities.subtitle') }}
        </p>
      </div>
    </div>

    <div class="opportunities-carousel">
      <div class="carousel-track">
        @for (opportunity of opportunities; track $index) {
          <div
            class="opportunity-card"
            (click)="onOpportunityClick(opportunity)"
            [class.clickable]="opportunity.url"
            [style.cursor]="opportunity.url ? 'pointer' : 'default'"
            [title]="
              opportunity.url ? t('lang.investmentOpportunities.learnMore') : ''
            ">
            <div class="opportunity-image-container">
              <img
                [src]="opportunity.image"
                [alt]="
                  t('lang.investmentOpportunities.imageAlt') +
                  ' ' +
                  ($index + 1)
                "
                class="opportunity-image" />
              <div class="historical-gains">
                <div class="gains-label">
                  {{ t('lang.investmentOpportunities.historicalGainsLabel') }}
                </div>
                <div class="gains-value">
                  {{
                    t('lang.investmentOpportunities.historicalGains', {
                      min: formatPercentage(opportunity.historicalGainsMin),
                      max: formatPercentage(opportunity.historicalGainsMax),
                    })
                  }}
                </div>
              </div>
              <div class="opportunity-info-overlay">
                <div class="category-info">
                  <img
                    [src]="
                      opportunity.categoryKey === 'phonogram'
                        ? 'assets/icons/music-note.svg'
                        : opportunity.categoryKey === 'composition'
                          ? 'assets/icons/playlist.svg'
                          : 'assets/icons/music-cd.svg'
                    "
                    alt="Category"
                    class="category-icon" />
                  <span class="category-text">
                    {{
                      t(
                        'lang.investmentOpportunities.category.' +
                          opportunity.categoryKey
                      )
                    }}
                  </span>
                </div>
                <h3 class="opportunity-title">{{ opportunity.title }}</h3>
                <p class="opportunity-artist">{{ opportunity.artist }}</p>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- Music Investment Explanation Section -->
  <section class="music-explanation-section">
    <div class="section-container">
      <div class="explanation-content">
        <div class="explanation-text">
          <p class="explanation-subtitle">
            {{ t('lang.musicExplanation.subtitle') }}
          </p>
          <h2 class="explanation-title">
            {{
              t(
                'lang.musicExplanation.steps.' +
                  activeExplanationStep +
                  '.title'
              )
            }}
          </h2>
        </div>

        <div class="explanation-image">
          <div class="image-container" [class.changing]="isChangingStep">
            <img
              [src]="explanationSvgIcons[activeExplanationStep]"
              [alt]="'Step ' + (activeExplanationStep + 1)"
              class="explanation-svg animated-svg"
              (load)="onImageLoad()" />
          </div>
        </div>

        <div class="explanation-steps">
          <button
            *ngFor="let step of explanationSteps; let i = index"
            class="step-number"
            [class.active]="activeExplanationStep === i"
            (click)="setActiveExplanationStep(i)">
            {{ i + 1 }}
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Portfolio Section -->
  <section class="portfolio-section">
    <div class="section-container">
      <div class="portfolio-content">
        <div class="portfolio-image">
          <div
            class="image-container"
            [class.changing]="isChangingPortfolioTab">
            <mzic-artist-catalog
              [displayMode]="artistCatalogData[activePortfolioTab].displayMode"
              [artistImage]="artistCatalogData[activePortfolioTab].artistImage"
              [artistImageLink]="artistCatalogData[activePortfolioTab].artistImageLink"
              [artistName]="artistCatalogData[activePortfolioTab].artistName"
              [items]="artistCatalogData[activePortfolioTab].items"
              [title]="t('lang.portfolio.tabs.' + activePortfolioTab + '.title')"
              [description]="t('lang.portfolio.tabs.' + activePortfolioTab + '.description')"
              class="portfolio-catalog"></mzic-artist-catalog>
          </div>
        </div>

        <div class="portfolio-text">
          <h2 class="portfolio-title">
            {{ t('lang.portfolio.title') }}
          </h2>
          <p class="portfolio-subtitle">
            {{ t('lang.portfolio.subtitle') }}
          </p>

          <!-- Desktop tabs -->
          <div class="portfolio-tabs desktop-tabs">
            @for (tab of portfolioTabs; track $index) {
              <button
                class="portfolio-tab"
                [class.active]="activePortfolioTab === $index"
                (click)="setActivePortfolioTab($index)">
                <div class="tab-icon">
                  <img
                    [src]="tab.icon"
                    [alt]="t('lang.portfolio.tabs.' + $index + '.title')"
                    class="tab-icon-svg" />
                </div>
                <h3 class="tab-title">
                  {{ t('lang.portfolio.tabs.' + $index + '.title') }}
                </h3>
              </button>
            }
          </div>

          <!-- Mobile cards -->
          <div class="portfolio-mobile-cards">
            @for (tab of portfolioTabs; track $index) {
              <div class="mobile-card">
                @if ($index !== 0) {
                  <div class="card-badge">
                    {{ t('lang.portfolio.tabs.' + $index + '.badge') }}
                  </div>
                }
                <div class="card-header">
                  <div class="card-icon">
                    <img
                      [src]="tab.icon"
                      [alt]="t('lang.portfolio.tabs.' + $index + '.title')"
                      class="card-icon-svg" />
                  </div>
                  <h3 class="card-title">
                    {{ t('lang.portfolio.tabs.' + $index + '.title') }}
                  </h3>
                </div>
                <p class="card-description">
                  {{ t('lang.portfolio.tabs.' + $index + '.description') }}
                </p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- App Download Section -->
  <section class="app-download-section">
    <div class="app-download-container">
      <div class="app-download-content">
        <div class="download-text">
          <h2 class="download-title">
            {{ t('lang.appDownload.title') }}
          </h2>
          <p class="download-subtitle">
            {{ t('lang.appDownload.subtitle') }}
          </p>
          <p class="download-subtitle-mobile">
            {{ t('lang.appDownload.subtitleMobile') }}
          </p>

          <div class="app-badges">
            <!-- Desktop badges -->
            <a
              [href]="playStoreUrl"
              target="_blank"
              class="app-badge-link desktop-badge">
              <img
                [src]="googlePlayLogo"
                [alt]="t('lang.appDownload.googlePlayAlt')"
                class="badge-image" />
            </a>
            <a
              [href]="appStoreUrl"
              target="_blank"
              class="app-badge-link desktop-badge">
              <img
                [src]="appleLogo"
                [alt]="t('lang.appDownload.appStoreAlt')"
                class="badge-image" />
            </a>

            <!-- Mobile badges -->
            <a
              [href]="playStoreUrl"
              target="_blank"
              class="app-badge-link mobile-badge">
              <img
                [src]="googlePlayDownload"
                [alt]="t('lang.appDownload.googlePlayAlt')"
                class="badge-image" />
            </a>
            <a
              [href]="appStoreUrl"
              target="_blank"
              class="app-badge-link mobile-badge">
              <img
                [src]="appStoreDownload"
                [alt]="t('lang.appDownload.appStoreAlt')"
                class="badge-image" />
            </a>
          </div>
        </div>

        <div class="qr-code-container">
          <div class="qr-code-wrapper">
            <img [src]="qrCodeMzic" alt="QR Code" class="qr-code-image" />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- FAQ Section -->
  <section class="faq-section">
    <div class="section-container">
      <div class="faq-content">
        <h2 class="faq-title">{{ t('lang.faq.title') }}</h2>

        <div class="faq-grid">
          <div class="faq-column">
            <div class="faq-item" [class.active]="activeFAQ === 'o-que-e-mzic'">
              <button class="faq-question" (click)="toggleFAQ('o-que-e-mzic')">
                <span>{{ t('lang.faq.questions.whatIsMzic.question') }}</span>
                <span
                  class="faq-icon"
                  [class.rotated]="activeFAQ === 'o-que-e-mzic'">
                  ↗
                </span>
              </button>
              <div
                class="faq-answer"
                [class.expanded]="activeFAQ === 'o-que-e-mzic'">
                <p>{{ t('lang.faq.questions.whatIsMzic.answer') }}</p>
                <a
                  (click)="goToAboutMzic()"
                  class="faq-link"
                  style="cursor: pointer">
                  {{ t('lang.faq.questions.whatIsMzic.link') }}
                </a>
              </div>
            </div>

            <div
              class="faq-item"
              [class.active]="activeFAQ === 'direitos-musicais'">
              <button
                class="faq-question"
                (click)="toggleFAQ('direitos-musicais')">
                <span>{{ t('lang.faq.questions.musicRights.question') }}</span>
                <span
                  class="faq-icon"
                  [class.rotated]="activeFAQ === 'direitos-musicais'">
                  ↗
                </span>
              </button>
              <div
                class="faq-answer"
                [class.expanded]="activeFAQ === 'direitos-musicais'">
                <p
                  *ngFor="
                    let answer of t('lang.faq.questions.musicRights.answers')
                  ">
                  {{ answer }}
                </p>
              </div>
            </div>

            <div
              class="faq-item"
              [class.active]="activeFAQ === 'tokens-participacao'">
              <button
                class="faq-question"
                (click)="toggleFAQ('tokens-participacao')">
                <span>
                  {{ t('lang.faq.questions.participationTokens.question') }}
                </span>
                <span
                  class="faq-icon"
                  [class.rotated]="activeFAQ === 'tokens-participacao'">
                  ↗
                </span>
              </button>
              <div
                class="faq-answer"
                [class.expanded]="activeFAQ === 'tokens-participacao'">
                <p
                  *ngFor="
                    let answer of t(
                      'lang.faq.questions.participationTokens.answers'
                    )
                  ">
                  {{ answer }}
                </p>
              </div>
            </div>
          </div>

          <div class="faq-column">
            <div
              class="faq-item"
              [class.active]="activeFAQ === 'como-investir'">
              <button class="faq-question" (click)="toggleFAQ('como-investir')">
                <span>{{ t('lang.faq.questions.howToInvest.question') }}</span>
                <span
                  class="faq-icon"
                  [class.rotated]="activeFAQ === 'como-investir'">
                  ↗
                </span>
              </button>
              <div
                class="faq-answer"
                [class.expanded]="activeFAQ === 'como-investir'">
                <p>{{ t('lang.faq.questions.howToInvest.answer') }}</p>
              </div>
            </div>

            <div
              class="faq-item"
              [class.active]="activeFAQ === 'vender-investimento'">
              <button
                class="faq-question"
                (click)="toggleFAQ('vender-investimento')">
                <span>
                  {{ t('lang.faq.questions.canSellInvestment.question') }}
                </span>
                <span
                  class="faq-icon"
                  [class.rotated]="activeFAQ === 'vender-investimento'">
                  ↗
                </span>
              </button>
              <div
                class="faq-answer"
                [class.expanded]="activeFAQ === 'vender-investimento'">
                <p>{{ t('lang.faq.questions.canSellInvestment.answer') }}</p>
              </div>
            </div>

            <div
              class="faq-item"
              [class.active]="activeFAQ === 'dividendos-royalties'">
              <button
                class="faq-question"
                (click)="toggleFAQ('dividendos-royalties')">
                <span>
                  {{ t('lang.faq.questions.royaltyPayments.question') }}
                </span>
                <span
                  class="faq-icon"
                  [class.rotated]="activeFAQ === 'dividendos-royalties'">
                  ↗
                </span>
              </button>
              <div
                class="faq-answer"
                [class.expanded]="activeFAQ === 'dividendos-royalties'">
                <p>{{ t('lang.faq.questions.royaltyPayments.answer') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Partners Section -->
  <section class="partners-section">
    <div class="partners-container">
      <div class="partners-content">
        <div class="partners-group">
          <h3 class="partners-title">{{ t('lang.partners.title') }}</h3>
          <div class="partners-logos">
            <img
              [src]="transferoLogo"
              alt="Transfero"
              class="partner-logo transfero" />

            <img
              [src]="vbsoLogo"
              alt="VBSO Advogados"
              class="partner-logo vbso" />
            <img
              [src]="mercadoBitcoinLogo"
              alt="Mercado Bitcoin"
              class="partner-logo mercado-bitcoin" />
          </div>
        </div>

        <div class="partners-group">
          <h3 class="partners-title">{{ t('lang.partners.cvm.title') }}</h3>
          <div class="partners-logos">
            <img [src]="cvmLogo" alt="CVM" class="partner-logo cvm" />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Footer Section -->
  <footer class="footer-section">
    <div class="footer-container">
      <div class="footer-content">
        <div class="footer-logo-section">
          <div class="footer-logo">
            <img
              src="assets/icons/mzic-logo-name.svg"
              alt="MZIC"
              class="logo-image" />
          </div>
        </div>

        <div class="footer-links-section">
          <div class="footer-column">
            <h3 class="footer-title">{{ t('lang.footer.company.title') }}</h3>
            <ul class="footer-links">
              <li>
                <a (click)="goToAboutMzic()" class="footer-link">
                  {{ t('lang.footer.company.aboutMzic') }}
                </a>
              </li>
              <li>
                <a
                  [href]="footerLinks.forArtists"
                  class="footer-link"
                  target="_blank">
                  {{ t('lang.footer.company.forArtists') }}
                </a>
              </li>
              <li>
                <a (click)="goToContact()" class="footer-link">
                  {{ t('lang.footer.company.contact') }}
                </a>
              </li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ t('lang.footer.legal.title') }}</h3>
            <ul class="footer-links">
              <li>
                <a
                  [href]="footerLinks.termsOfUse"
                  class="footer-link"
                  target="_blank">
                  {{ t('lang.footer.legal.termsOfUse') }}
                </a>
              </li>
              <li>
                <a
                  [href]="footerLinks.privacyPolicy"
                  class="footer-link"
                  target="_blank">
                  {{ t('lang.footer.legal.privacyPolicy') }}
                </a>
              </li>
            </ul>
          </div>

          <div class="footer-column app-column">
            <h3 class="footer-title">
              {{ t('lang.footer.downloadApp.title') }}
            </h3>
            <div class="app-buttons">
              <a [href]="playStoreUrl" target="_blank" class="app-button">
                <img
                  [src]="googlePlayDownload"
                  [alt]="t('lang.appDownload.googlePlayAlt')"
                  class="app-badge" />
              </a>
              <a [href]="appStoreUrl" target="_blank" class="app-button">
                <img
                  [src]="appStoreDownload"
                  [alt]="t('lang.appDownload.appStoreAlt')"
                  class="app-badge" />
              </a>
            </div>
          </div>
        </div>

        <div class="footer-social">
          <a
            [href]="socialLinks.instagram"
            class="social-link"
            aria-label="Instagram"
            target="_blank">
            <img
              src="https://mzic-website.s3.us-east-1.amazonaws.com/mzic-instagram-white.svg"
              alt="Instagram"
              width="24"
              height="24" />
          </a>
          <a
            [href]="socialLinks.youtube"
            class="social-link"
            aria-label="YouTube"
            target="_blank">
            <img
              src="https://mzic-website.s3.us-east-1.amazonaws.com/mzic-youtube-white.svg"
              alt="YouTube"
              width="24"
              height="24" />
          </a>
          <a
            [href]="socialLinks.spotify"
            class="social-link"
            aria-label="Spotify"
            target="_blank">
            <img
              src="https://mzic-website.s3.us-east-1.amazonaws.com/mzic-spotify-white.svg"
              alt="Spotify"
              width="24"
              height="24" />
          </a>
          <a
            [href]="socialLinks.linkedin"
            class="social-link"
            aria-label="LinkedIn"
            target="_blank">
            <img
              src="https://mzic-website.s3.us-east-1.amazonaws.com/mzic-linkedin-white.svg"
              alt="LinkedIn"
              width="24"
              height="24" />
          </a>
        </div>
      </div>

      <div class="footer-bottom">
        <div class="footer-disclaimer">
          <p class="disclaimer-text">
            {{ t('lang.footer.disclaimer') }}
          </p>

          <br />

          <p class="disclaimer-text">
            {{ t('lang.footer.disclaimer2') }}
          </p>
        </div>

        <div class="footer-address">
          <div class="address-info">
            <span class="address">
              Av. João Jorge Saad, 305 – Vila Progredior, São Paulo – SP,
              05618-000.
            </span>
          </div>
          <div class="company-info">
            <span class="company">
              © 2025 MZIC Brasil S.A. 60.975.631/0001-85
            </span>
          </div>
        </div>
      </div>
    </div>
  </footer>
</ng-container>
